// src/components/ProjectDetailModal.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ExternalLink, MessageSquare, Tag } from 'lucide-react';
import './BlogPostContent.css'; // Reuse blog post styles for HTML details

const IMAGE_BASE_URL = 'https://smartsolutionslk.com/uploads/';

const ProjectDetailModal = ({ project, onClose }) => {
    // Escape key listener to close modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    if (!project) return null;

    // Helper to get image URL
    const getImageSrc = (url) => {
        if (!url) return 'https://placehold.co/1200x800/1f2937/FFFFFF?text=No+Image';
        if (url.startsWith('data:image')) return url;
        if (url.startsWith('http')) return url;
        return `${IMAGE_BASE_URL}${url}`;
    };

    // Auto-split title: first word in white/dark, rest in cyan
    const titleWords = project.title ? project.title.split(' ') : [];
    const firstWord = titleWords[0] || '';
    const restOfTitle = titleWords.slice(1).join(' ');

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', duration: 0.4, bounce: 0.15 } },
        exit: { opacity: 0, scale: 0.95, y: 15, transition: { duration: 0.25, ease: 'easeIn' } }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 sm:p-6 md:p-10 backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="bg-white dark:bg-gray-950 rounded-2xl w-full max-w-5xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto shadow-2xl relative border border-slate-200 dark:border-gray-800"
                onClick={(e) => e.stopPropagation()} // Prevent close on interior click
            >
                    {/* Header Action Row (Floating Close Button) */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-20 p-2.5 rounded-full bg-black/40 text-white hover:bg-cyan-500 hover:text-black transition-all duration-300 backdrop-blur-md"
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button>

                    {/* Project Hero Banner / Image */}
                    <div className="relative w-full h-[250px] sm:h-[350px] md:h-[400px] bg-slate-900 overflow-hidden">
                        <img
                            src={getImageSrc(project.image_url || project.image)}
                            alt={project.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/1200x800/1f2937/FFFFFF?text=Image+Not+Found';
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                        
                        {/* Title and Category on top of Hero Image */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 flex flex-col justify-end">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/25 border border-cyan-500/40 text-cyan-300 text-xs font-bold w-fit mb-3 backdrop-blur-sm">
                                <Tag size={12} />
                                {project.category}
                            </span>
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                                <span>{firstWord} </span>
                                <span className="text-cyan-400">{restOfTitle}</span>
                            </h2>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 sm:p-8 md:p-10 space-y-8">
                        
                        {/* Short Description */}
                        {project.description && (
                            <p className="text-lg sm:text-xl font-medium text-slate-600 dark:text-gray-300 leading-relaxed border-l-4 border-cyan-500 pl-4 py-1 bg-cyan-500/5 dark:bg-cyan-500/5 rounded-r">
                                {project.description}
                            </p>
                        )}

                        {/* Detailed Project Info (HTML / Rich Text) */}
                        {project.details ? (
                            <div className="blog-post-content text-slate-800 dark:text-gray-300">
                                <div dangerouslySetInnerHTML={{ __html: project.details }} />
                            </div>
                        ) : (
                            // Fallback if no rich-text details are present yet
                            <div className="text-slate-500 dark:text-gray-400">
                                <p>No additional project details are currently available for this project.</p>
                            </div>
                        )}

                        {/* Divider */}
                        {(project.client_feedback || project.project_link) && (
                            <div className="h-px bg-slate-200 dark:bg-gray-800 my-8" />
                        )}

                        {/* Client Feedback section */}
                        {project.client_feedback && (
                            <div className="relative p-6 sm:p-8 rounded-xl bg-slate-50 dark:bg-gray-900 border border-slate-100 dark:border-gray-800/80 overflow-hidden">
                                <div className="absolute -top-3 -left-3 text-cyan-500/10 dark:text-cyan-400/5 text-8xl font-serif pointer-events-none select-none">“</div>
                                <h4 className="text-sm font-bold tracking-widest text-cyan-500 uppercase mb-4 flex items-center gap-2">
                                    <MessageSquare size={16} />
                                    Client Feedback
                                </h4>
                                <blockquote className="text-base sm:text-lg italic text-slate-700 dark:text-gray-300 leading-relaxed relative z-10">
                                    "{project.client_feedback}"
                                </blockquote>
                            </div>
                        )}

                        {/* Footer Link / CTA */}
                        {project.project_link && (
                            <div className="flex justify-end pt-4">
                                <a
                                    href={project.project_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2.5 px-6 py-3.5 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-xl shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20 transform hover:-translate-y-0.5 transition-all duration-200"
                                >
                                    Visit Project Website
                                    <ExternalLink size={18} />
                                </a>
                            </div>
                        )}

                    </div>
                </motion.div>
        </motion.div>
    );
};

export default ProjectDetailModal;
